import type { QueryHookOptions } from "@apollo/client";
import { useQuery } from "@apollo/client";

import { getFragmentData } from "../../__generated__/fragment-masking";
import type {
  ClientSubscriptionQuery,
  ClientSubscriptionQueryVariables,
  SubscriptionFieldsFragment,
} from "../../__generated__/graphql";
import {
  ClientSubscriptionDocument,
  SubscriptionFieldsFragmentDoc,
} from "../../__generated__/graphql";
import { useClientId } from "../../shared";

/** Unmasked `Client.subscription` shape consumed by the subscription slice. */
export type ClientSubscription = SubscriptionFieldsFragment;
export type ClientSubscriptionStatus = ClientSubscription["status"];

// Defaults to the current client; `clientId` in options is only for external
// callers (host surfaces) that pass their own until luce-fe migrates.
export function useClientSubscription(
  options?: QueryHookOptions<ClientSubscriptionQuery, ClientSubscriptionQueryVariables> & {
    clientId?: string;
  },
) {
  const currentClientId = useClientId();
  const { clientId: explicitClientId, ...queryOptions } = options ?? {};
  const clientId = explicitClientId ?? currentClientId;
  const { data, loading, refetch } = useQuery(ClientSubscriptionDocument, {
    fetchPolicy: "cache-first",
    ...queryOptions,
    skip: !clientId || queryOptions.skip,
    variables: { id: clientId },
  });

  const subscription = getFragmentData(SubscriptionFieldsFragmentDoc, data?.client.subscription);

  return { subscription: subscription ?? null, isSubscriber: !!subscription, loading, refetch };
}
